import { Component, OnInit, ViewEncapsulation, Injector } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { environment } from '../../../services/global-environment';
import {
  LawData,
  LawApiResponse,
  LawDataStats,
  LawSortDirection,
  LawFilterStatus
} from '../../../models/law-management.model';
import { LawDetailsModalComponent } from './law-details-modal/law-details-modal.component';

@Component({
  selector: 'app-law-management',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './law-management.component.html',
  styleUrls: ['./law-management.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LawManagementComponent implements OnInit {
  // 資料相關
  lawData: LawData[] = [];
  filteredData: LawData[] = [];
  loading = false;
  error: string | null = null;
  lastUpdated: string = '';

  // 統計資料
  stats: LawDataStats = {
    normal: 0,
    failed: 0,
    empty: 0,
    total: 0
  };

  // 法規來源統計
  sourceStats: { [key: string]: number } = {};

  // 篩選相關
  filterStatus: LawFilterStatus = 'all';
  filterSource: string = 'all';
  searchKeyword = '';
  startDate = '';
  endDate = '';

  // 排序相關
  sortField = '';
  sortDirection: LawSortDirection = 'asc';

  // 彈出視窗相關
  isPopupVisible = false;
  selectedLaw: any = null;

  // 備註編輯相關
  editingItemId: string | null = null;
  editingMemoText: string = '';

  private apiUrl = environment.apiUrl;
  private overlayRef: OverlayRef | null = null;

  constructor(
    private http: HttpClient,
    private overlay: Overlay,
    private injector: Injector
  ) {}

  ngOnInit(): void {
    this.loadLawData();
  }

  // 載入法規資料
  loadLawData(): void {
    this.loading = true;
    this.error = null;

    const url = `${this.apiUrl}/api/law_list_shared/get_all`;

    this.http.get<LawApiResponse>(url).pipe(
      map(response => {

        console.log('API 回應:', response);

        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'API 回應格式錯誤');
      }),
      catchError(error => {
        console.error('載入法規資料失敗:', error);
        this.error = '載入法規資料失敗，請稍後再試';
        return [];
      })
    ).subscribe({
      next: (data) => {
        this.lawData = data;

        // 調試：檢查資料結構
        if (data.length > 0) {
          console.log('第一筆資料結構:', data[0]);
          console.log('ID 欄位:', data[0].id);
        }

        this.calculateStats();
        this.applyFilters();
        this.lastUpdated = new Date().toLocaleString('zh-TW');
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
      }
    });
  }

  // 計算統計資料
  calculateStats(): void {
    this.stats = {
      normal: 0,
      failed: 0,
      empty: 0,
      total: this.lawData.length
    };

    // 重置法規來源統計
    this.sourceStats = {};

    this.lawData.forEach(item => {
      // 計算狀態統計
      if (item.laws.fetch_status === 'failed') {
        this.stats.failed++;
      } else if (item.laws.law_count === 0) {
        this.stats.empty++;
      } else {
        this.stats.normal++;
      }

      // 計算法規來源統計
      const sourceName = this.getSourceWebsiteName(item.laws.law_url);
      if (sourceName) {
        this.sourceStats[sourceName] = (this.sourceStats[sourceName] || 0) + 1;
      }
    });
  }

  // 套用篩選條件
  applyFilters(): void {
    let filtered = [...this.lawData];

    // 狀態篩選
    if (this.filterStatus !== 'all') {
      filtered = filtered.filter(item => {
        switch (this.filterStatus) {
          case 'normal':
            return item.laws.fetch_status !== 'failed' && item.laws.law_count > 0;
          case 'failed':
            return item.laws.fetch_status === 'failed';
          case 'empty':
            return item.laws.law_count === 0;
          default:
            return true;
        }
      });
    }

    // 法規來源篩選
    if (this.filterSource !== 'all') {
      filtered = filtered.filter(item => {
        const sourceName = this.getSourceWebsiteName(item.laws.law_url);
        return sourceName === this.filterSource;
      });
    }

    // 關鍵字搜尋
    if (this.searchKeyword.trim()) {
      const keyword = this.searchKeyword.toLowerCase();
      filtered = filtered.filter(item =>
        (item.laws.law_title && item.laws.law_title.toLowerCase().includes(keyword)) ||
        (item.laws.law_name && item.laws.law_name.toLowerCase().includes(keyword)) ||
        item.laws.law_url.toLowerCase().includes(keyword)
      );
    }

    // 日期區間篩選
    if (this.startDate || this.endDate) {
      filtered = filtered.filter(item => {
        const itemDate = item.laws.law_version;
        if (this.startDate && this.endDate) {
          return itemDate >= this.startDate && itemDate <= this.endDate;
        } else if (this.startDate) {
          return itemDate >= this.startDate;
        } else if (this.endDate) {
          return itemDate <= this.endDate;
        }
        return true;
      });
    }

    this.filteredData = filtered;
    this.applySorting();
  }

  // 套用排序
  applySorting(): void {
    if (!this.sortField) {
      return;
    }

    this.filteredData.sort((a, b) => {
      let valueA: any, valueB: any;

      switch (this.sortField) {
        case 'ScanDate':
          valueA = a.ScanDate;
          valueB = b.ScanDate;
          break;
        case 'law_version':
          valueA = a.laws.law_version;
          valueB = b.laws.law_version;
          break;
        case 'law_title':
          valueA = a.laws.law_title || a.laws.law_name || '';
          valueB = b.laws.law_title || b.laws.law_name || '';
          break;
        case 'law_count':
          valueA = a.laws.law_count;
          valueB = b.laws.law_count;
          break;
        case 'fetch_status':
          valueA = a.laws.fetch_status;
          valueB = b.laws.fetch_status;
          break;
        default:
          return 0;
      }

      if (valueA < valueB) {
        return this.sortDirection === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  // 點擊狀態篩選
  onStatusFilter(status: LawFilterStatus): void {
    this.filterStatus = status;
    this.filterSource = 'all'; // 重置來源篩選
    this.applyFilters();
  }

  // 點擊法規來源篩選
  onSourceFilter(source: string): void {
    this.filterSource = source;
    this.filterStatus = 'all'; // 重置狀態篩選
    this.applyFilters();
  }

  // 搜尋關鍵字變更
  onSearchChange(): void {
    this.applyFilters();
  }

  // 日期篩選變更
  onDateFilterChange(): void {
    this.applyFilters();
  }

  // 清除日期篩選
  clearDateFilter(): void {
    this.startDate = '';
    this.endDate = '';
    this.applyFilters();
  }

  // 排序點擊
  onSortClick(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.applySorting();
  }

  // 取得排序指示器類別
  getSortIndicatorClass(field: string): string {
    if (this.sortField !== field) {
      return '';
    }
    return this.sortDirection === 'asc' ? 'sort-asc' : 'sort-desc';
  }

  // 判斷是否為當前排序欄位
  isCurrentSortField(field: string): boolean {
    return this.sortField === field;
  }

  // 取得狀態標籤
  getStatusLabel(item: LawData): string {
    if (item.laws.fetch_status === 'failed') {
      return '擷取失敗';
    } else if (item.laws.law_count === 0) {
      return '無內容';
    } else {
      return '正常';
    }
  }

  // 取得狀態樣式類別
  getStatusClass(item: LawData): string {
    if (item.laws.fetch_status === 'failed') {
      return 'status-failed';
    } else if (item.laws.law_count === 0) {
      return 'status-empty';
    } else {
      return 'status-normal';
    }
  }

  // 重新載入資料
  refresh(): void {
    this.loadLawData();
  }

  // 取得法規標題或名稱
  getLawTitle(item: LawData): string {
    return item.laws.law_name || item.laws.law_title || '無標題';
  }

  // 取得法規來源網站名稱
  getSourceWebsiteName(lawUrl: string): string {
    // 如果沒有URL，返回空字符串
    if (!lawUrl) return '未設定';

    try {
      // 解析URL獲取hostname
      const url = new URL(lawUrl);
      const hostname = url.hostname;

      // 定義主要法規網站的映射表
      const hostMappings: Record<string, string> = {
        'law.moj.gov.tw': '全國法規資料庫',
        'laws.mol.gov.tw': '勞動部',
        'law.epa.gov.tw': '環境保護署',
        'laws.moj.gov.tw': '法務部',
        'mojlaw.moj.gov.tw': '法務部',
        'gazette.nat.gov.tw': '國家發展委員會',
        'osha.gov.tw': '職業安全衛生署',
        'law.coa.gov.tw': '農業委員會',
        'law.banking.gov.tw': '金融監督管理委員會',
        'laws.fsc.gov.tw': '金融監督管理委員會',
        'law.cdc.gov.tw': '疾病管制署',
        'law.nfa.gov.tw': '消防署',
        'law.dot.gov.tw': '交通部',
        'oaout.moenv.gov.tw': '環境部',
        'lawsearch.pcc.gov.tw': '公共工程委員會',
        'law.moea.gov.tw': '經濟部',
        'selaw.com.tw': '證卷暨期貨法令判解查詢系統',
        'moeaea.gov.tw': '經濟部能源署',
        'laws.taipei.gov.tw': '台北市法規查詢系統',
        'lawsearch.taichung.gov.tw': '台中市法規查詢系統',
        'law.nstc.gov.tw': '國科會',
        'law-out.mof.gov.tw': '財政部',
        'erss.nusc.gov.tw': '核能安全委員會',
        'outlaw.kcg.gov.tw': '高雄市法規查詢系統',
        'web.law.ntpc.gov.tw': '新北市法規查詢系統',
        'www.cdc.gov.tw':'衛服部'
      };

      // 檢查映射表中是否有對應的主機名稱
      if (hostname in hostMappings) {
        return hostMappings[hostname];
      }

      // 如果沒有直接匹配，嘗試移除 www 前綴再匹配
      const hostnameWithoutWww = hostname.startsWith('www.') ? hostname.substring(4) : hostname;
      if (hostnameWithoutWww in hostMappings) {
        return hostMappings[hostnameWithoutWww];
      }

      // 如果沒有映射則返回主機名稱
      return hostname;
    } catch (error) {
      console.error('解析URL時發生錯誤:', error);
      // 如果URL格式無效，直接返回"未設定"
      return '未設定';
    }
  }

  // 開啟法條內容彈出視窗
  openLawDetailsPopup(item: LawData): void {
    // 先檢查是否已有法條內容
    if (item.laws.law_articles && item.laws.law_articles.length > 0) {
      // 已有法條內容，直接開啟彈窗
      this.showLawDetailsModal(item.laws, false);
    } else {
      // 沒有法條內容，需要透過 API 取得
      this.fetchLawArticles(item.id, item.laws);
    }
  }

  // 透過 API 取得法規條文內容
  private fetchLawArticles(lawId: string, lawData: any): void {
    const companyId = environment.companyid || 'shared';
    const url = `${this.apiUrl}/api/law_list_shared/get_law_articles/${companyId}/${lawId}`;

    console.log(`正在取得法規條文內容: ${url}`);

    // 先開啟模態框，顯示載入狀態
    const modalRef = this.showLawDetailsModal(lawData, true);

    this.http.get<any>(url).pipe(
      catchError(error => {
        console.error('取得法規條文內容失敗:', error);

        // 更新模態框的載入狀態和錯誤訊息
        if (modalRef) {
          modalRef.isLoading = false;
          modalRef.hasError = true;

          // 根據錯誤類型設定錯誤訊息
          if (error.status === 404) {
            modalRef.errorMessage = '找不到指定的法規資料';
          } else if (error.status === 429) {
            modalRef.errorMessage = '請求過於頻繁，請稍後再試';
          } else if (error.status === 400) {
            modalRef.errorMessage = '請求參數錯誤';
          } else {
            modalRef.errorMessage = '無法載入法規條文內容';
          }
        }

        return of(null);
      })
    ).subscribe({
      next: (response) => {


        if (response && response.success && response.data) {
          // API 回應的資料結構是完整的文檔，其中 laws.law_articles 包含法條內容
          const fullDocument = response.data;

          // 將取得的法條內容填入原始資料
          if (fullDocument.laws && fullDocument.laws.law_articles) {
            lawData.law_articles = fullDocument.laws.law_articles;

            // 更新模態框的資料和載入狀態
            if (modalRef) {
              modalRef.data = lawData;
              modalRef.isLoading = false;
              modalRef.hasError = false;
            }

            console.log(`成功取得法規條文 ${fullDocument.laws.law_articles.length} 條`);
          } else {
            // 沒有找到法條內容
            if (modalRef) {
              modalRef.isLoading = false;
              modalRef.hasError = true;
              modalRef.errorMessage = '未找到法條內容';
            }
            console.warn('API 回應中沒有法條內容');
          }
        } else {
          // API 回應格式錯誤
          if (modalRef) {
            modalRef.isLoading = false;
            modalRef.hasError = true;
            modalRef.errorMessage = 'API 回應格式錯誤';
          }
        }
      },
      error: (error) => {
        console.error('API 請求失敗:', error);
        if (modalRef) {
          modalRef.isLoading = false;
          modalRef.hasError = true;
          modalRef.errorMessage = 'API 請求失敗';
        }
      }
    });
  }

  // 顯示法條詳細資料的模態框
  private showLawDetailsModal(lawData: any, isLoading: boolean = false): any {
    // 創建 Overlay 配置
    const overlayConfig = {
      hasBackdrop: false, // 我們自己處理背景
      positionStrategy: this.overlay.position()
        .global()
        .centerHorizontally()
        .centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block()
    };

    // 創建 Overlay
    this.overlayRef = this.overlay.create(overlayConfig);

    // 創建注入器的提供者陣列
    const providers = [
      { provide: OverlayRef, useValue: this.overlayRef },
      { provide: 'LAW_DATA', useValue: lawData }
    ];

    // 只在需要時添加 IS_LOADING 提供者
    if (isLoading) {
      providers.push({ provide: 'IS_LOADING', useValue: isLoading });
    }

    // 創建 Injector 來傳遞資料
    const injector = Injector.create({
      parent: this.injector,
      providers: providers
    });

    // 創建 ComponentPortal
    const portal = new ComponentPortal(LawDetailsModalComponent, null, injector);

    // 附加組件到 Overlay
    const componentRef = this.overlayRef.attach(portal);

    // 返回組件實例，以便後續更新
    return componentRef.instance;
  }

  // 關閉彈出視窗
  closePopup(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.isPopupVisible = false;
    this.selectedLaw = null;
  }

  // 點擊背景關閉彈窗
  onBackgroundClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.closePopup();
    }
  }

  // 格式化法條內容
  formatArticleContent(content: string): string {
    if (!content) return '';
    // 將換行符號轉換為 HTML 換行
    return content.replace(/\n/g, '<br>');
  }

  // 計算實際法條數量
  getActualLawCount(item: LawData): number {
    // 直接使用 law_count 欄位，這是已經計算好的條文數量
    return item.laws.law_count || 0;
  }

  // 取得法規來源統計列表
  getSourceStatsList(): { name: string; count: number }[] {
    return Object.entries(this.sourceStats)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count); // 按數量降序排序
  }

  // 備註編輯相關方法
  isEditingMemo(item: LawData): boolean {
    return this.editingItemId === item.id;
  }

  startEditMemo(item: LawData): void {
    this.editingItemId = item.id;
    this.editingMemoText = item.check_memo || '';

    // 使用 setTimeout 確保 input 元素已經渲染
    setTimeout(() => {
      const input = document.querySelector('.memo-edit input') as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  saveMemo(item: LawData): void {
    // 取得編輯的文字（去除前後空白）
    const newMemo = this.editingMemoText.trim();

    // 檢查內容是否有變更
    if (newMemo !== (item.check_memo || '')) {
      // 暫存原始值，以便錯誤時還原
      const originalMemo = item.check_memo;

      // 發送 API 請求更新資料庫
      const url = `${this.apiUrl}/api/law_list_shared/update_check_memo`;
      const payload = {
        id: item.id,
        check_memo: newMemo
      };

      this.http.put(url, payload).subscribe({
        next: (response: any) => {
          console.log('備註更新成功', response);
          // 更新成功後，更新本地資料
          item.check_memo = newMemo;

          // 更新原始資料陣列中的對應項目
          const index = this.lawData.findIndex(data => data.id === item.id);
          if (index !== -1) {
            this.lawData[index].check_memo = newMemo;
          }
        },
        error: (error) => {
          console.error('備註更新失敗:', error);

          // 顯示錯誤訊息
          let errorMessage = '備註更新失敗';
          if (error.status === 404) {
            errorMessage = '找不到指定的法規資料';
          } else if (error.status === 429) {
            errorMessage = '請求過於頻繁，請稍後再試';
          } else if (error.status === 400) {
            errorMessage = '請求參數錯誤';
          }

          // 可以在這裡加入錯誤提示，例如顯示 alert 或 toast
          alert(errorMessage);
        }
      });
    }

    // 關閉編輯模式
    this.cancelEditMemo();
  }

  cancelEditMemo(): void {
    this.editingItemId = null;
    this.editingMemoText = '';
  }
}
